import type { ComponentType } from 'react'

// Core notification types
export type NotificationType
  = | 'candidate_added_referees'
    | 'referee_viewed_reference'
    | 'referee_started_reference'
    | 'referee_completed_reference'
    | 'reference_process_completed'
    | 'reference_request_ready_to_start'
    | 'system_announcement'
    | 'weekly_summary'

export type NotificationStatus = 'read' | 'unread'
export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent'

// Core interfaces
export interface Notification {
  id: string
  userId: string
  workspaceId: string
  candidateId?: string | null
  refereeId?: string | null
  type: NotificationType
  title: string
  message: string
  data?: Record<string, unknown>
  createdAt: Date | string
  readAt?: Date | string | null
  deletedAt?: Date | string | null
}

export interface NotificationWithUser extends Notification {
  user?: {
    id: string
    name: string
    email: string
    avatar?: string
  }
}

// Filter interfaces
export interface NotificationFilter {
  type?: NotificationType
  status?: NotificationStatus
  dateFrom?: Date
  dateTo?: Date
  search?: string
}

// Component prop interfaces
export interface NotificationItemProps {
  notification: Notification | NotificationWithUser
  onMarkAsRead?: (id: string) => void
  onMarkAsUnread?: (id: string) => void
  onClick?: (notification: Notification) => void
  className?: string
  selected?: boolean
  onSelect?: (id: string, selected: boolean) => void
  showUnreadAction?: boolean
}

export interface NotificationBellProps {
  unreadCount?: number
  onClick?: () => void
  className?: string
  size?: 'sm' | 'default' | 'lg'
}

export interface NotificationDropdownProps {
  notifications: Notification[]
  unreadCount: number
  onMarkAsRead: (id: string) => Promise<void> | void
  onMarkAllAsRead: () => Promise<void> | void
  onViewAll: () => void
  onOpen?: () => void
  loading?: boolean
  error?: Error | null
  className?: string
}

export interface NotificationCenterProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  notifications: Notification[]
  loading?: boolean
  onMarkAsRead: (ids: string[]) => Promise<void> | void
  onMarkAllAsRead: () => Promise<void> | void
  onLoadMore?: () => Promise<void> | void
  hasMore?: boolean
  totalCount?: number
}

// API response types (removed duplicates - use from lib/api/notifications.ts instead)

export interface PaginationMeta {
  page: number
  pageSize: number
  totalPages: number
  totalCount: number
  hasMore?: boolean
}

// Notification preferences
export interface NotificationPreferences {
  email: boolean
  inApp: boolean
  types: Partial<Record<NotificationType, boolean>>
}

// Constants
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  candidate_added_referees: 'Referees Added',
  referee_viewed_reference: 'Reference Viewed',
  referee_started_reference: 'Reference Started',
  referee_completed_reference: 'Reference Completed',
  reference_process_completed: 'Process Completed',
  reference_request_ready_to_start: 'Ready To Start',
  system_announcement: 'Announcement',
  weekly_summary: 'Weekly Summary',
}

export const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10, // Align with API's pageSize instead of limit
}

// Icon mapping type
export interface NotificationIconMapping {
  [key: string]: {
    icon: ComponentType<{ className?: string }>
    color: string
  }
}

// Hook return types are defined in the hooks themselves to avoid duplication
// API request/response types are defined in lib/api/notifications.ts
